import { useState, useEffect, useCallback } from 'react'
import {
  X, Loader2, Save, CheckCircle, PenTool,
  ClipboardList, AlertTriangle, Share2, Copy, Check
} from 'lucide-react'
import api from '../../core/services/api'
import { Toast } from '../Toast'
import type { SchedaIstanzaDetail as IstanzaDetail } from '../../types/schede'
import { STATO_ISTANZA_CONFIG } from '../../types/schede'
import FormRenderer from './renderer/FormRenderer'
import FirmaSempliceModal from './FirmaSempliceModal'

interface SchedaIstanzaDetailProps {
  istanzaId: string
  onClose: () => void
}

export default function SchedaIstanzaDetail({ istanzaId, onClose }: SchedaIstanzaDetailProps) {
  const [istanza, setIstanza] = useState<IstanzaDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [completing, setCompleting] = useState(false)
  const [dati, setDati] = useState<Record<string, any>>({})
  const [isDirty, setIsDirty] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [showFirma, setShowFirma] = useState(false)
  const [firmaLoading, setFirmaLoading] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [shareLoading, setShareLoading] = useState(false)
  const [shareLink, setShareLink] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    loadIstanza()
  }, [istanzaId])

  const loadIstanza = async () => {
    try {
      const res = await api.get(`/v1/schede-dinamiche/istanze/${istanzaId}`)
      setIstanza(res.data)
      setDati(res.data.dati || {})
    } catch (err: any) {
      setToast({ message: err.response?.data?.detail || 'Errore nel caricamento', type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleFieldChange = useCallback((fieldId: string, value: any) => {
    setDati(prev => ({ ...prev, [fieldId]: value }))
    setIsDirty(true)
    setErrors(prev => {
      if (prev[fieldId]) {
        const next = { ...prev }
        delete next[fieldId]
        return next
      }
      return prev
    })
  }, [])

  const handleSave = async () => {
    if (!istanza) return
    setSaving(true)
    try {
      await api.put(`/v1/schede-dinamiche/istanze/${istanza.id}`, { dati })
      setIsDirty(false)
      setToast({ message: 'Scheda salvata', type: 'success' })
      // Refresh
      const res = await api.get(`/v1/schede-dinamiche/istanze/${istanza.id}`)
      setIstanza(res.data)
    } catch (err: any) {
      setToast({ message: err.response?.data?.detail || 'Errore nel salvataggio', type: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const handleComplete = async () => {
    if (!istanza) return
    // Save first if dirty
    if (isDirty) {
      setSaving(true)
      try {
        await api.put(`/v1/schede-dinamiche/istanze/${istanza.id}`, { dati })
        setIsDirty(false)
      } catch (err: any) {
        setToast({ message: err.response?.data?.detail || 'Errore nel salvataggio', type: 'error' })
        setSaving(false)
        return
      }
      setSaving(false)
    }

    setCompleting(true)
    try {
      await api.post(`/v1/schede-dinamiche/istanze/${istanza.id}/completa`)
      setToast({ message: 'Scheda completata', type: 'success' })
      const res = await api.get(`/v1/schede-dinamiche/istanze/${istanza.id}`)
      setIstanza(res.data)
      setErrors({})
    } catch (err: any) {
      const detail = err.response?.data?.detail
      // Backend returns validation errors as "Campi obbligatori mancanti: field1, field2"
      if (typeof detail === 'string' && detail.includes('obbligatori')) {
        setToast({ message: detail, type: 'error' })
      } else {
        setToast({ message: detail || 'Errore nella validazione', type: 'error' })
      }
    } finally {
      setCompleting(false)
    }
  }

  const handleFirma = async (dataUrl: string, nominativo: string) => {
    if (!istanza) return
    setFirmaLoading(true)
    try {
      await api.post(`/v1/schede-dinamiche/istanze/${istanza.id}/firma-semplice`, {
        firma_base64: dataUrl,
        nominativo,
      })
      setShowFirma(false)
      setToast({ message: 'Scheda firmata', type: 'success' })
      const res = await api.get(`/v1/schede-dinamiche/istanze/${istanza.id}`)
      setIstanza(res.data)
    } catch (err: any) {
      setToast({ message: err.response?.data?.detail || 'Errore nella firma', type: 'error' })
    } finally {
      setFirmaLoading(false)
    }
  }

  const handleShareLink = async () => {
    if (!istanza) return
    setShareLoading(true)
    try {
      const res = await api.post('/v1/schede-pubbliche/genera-link', {
        istanza_id: istanza.id,
        durata_giorni: 30,
      })
      const { token, tenant_code } = res.data
      const baseUrl = window.location.origin
      const link = `${baseUrl}/public/scheda/${tenant_code}/${token}`
      setShareLink(link)
    } catch (err: any) {
      setToast({ message: err.response?.data?.detail || 'Errore generazione link', type: 'error' })
    } finally {
      setShareLoading(false)
    }
  }

  const handleCopyLink = () => {
    if (shareLink) {
      navigator.clipboard.writeText(shareLink)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  // Se edit_policy='view_only' e stato non e' bozza, la scheda e' in sola lettura
  const policyLocked = istanza?.edit_policy === 'view_only' && istanza?.stato !== 'bozza'
  const isReadOnly = policyLocked || istanza?.stato === 'firmata' || istanza?.stato === 'archiviata'
  const canComplete = istanza?.stato === 'bozza'
  const canSign = istanza?.stato === 'compilata'

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div
        className="w-full max-w-3xl max-h-[90vh] flex flex-col rounded-xl shadow-xl overflow-hidden border"
        style={{ backgroundColor: 'var(--color-bg-card)', borderColor: 'var(--color-border)' }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 border-b flex-shrink-0"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <ClipboardList className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--color-primary)' }} />
            <div className="min-w-0">
              <h3 className="text-base font-semibold truncate">
                {istanza?.template_nome || 'Caricamento...'}
              </h3>
              {istanza && (
                <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
                  <span>{istanza.codice}</span>
                  <span>·</span>
                  <span>v{istanza.versione_numero}</span>
                  {istanza.entity_label && (
                    <>
                      <span>·</span>
                      <span className="truncate">{istanza.entity_label}</span>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {istanza && (
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${STATO_ISTANZA_CONFIG[istanza.stato]?.bg || ''} ${STATO_ISTANZA_CONFIG[istanza.stato]?.color || ''}`}>
                {STATO_ISTANZA_CONFIG[istanza.stato]?.label || istanza.stato}
              </span>
            )}
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg transition-colors hover:opacity-80"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--color-primary)' }} />
            </div>
          ) : !istanza ? (
            <div className="text-center py-12">
              <AlertTriangle className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--color-danger, #ef4444)' }} />
              <p className="text-sm">Errore nel caricamento della scheda</p>
            </div>
          ) : (
            <>
              <FormRenderer
                schema={istanza.schema_frozen}
                dati={dati}
                onChange={handleFieldChange}
                readOnly={isReadOnly}
                errors={errors}
              />

              {/* Firma esistente */}
              {istanza.firma_semplice && (
                <div className="mt-6 pt-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <PenTool className="w-4 h-4" style={{ color: 'var(--color-success, #22c55e)' }} />
                    <span className="text-sm font-medium">Firma</span>
                  </div>
                  <div className="rounded-lg border p-3" style={{ borderColor: 'var(--color-border)' }}>
                    <img
                      src={istanza.firma_semplice}
                      alt="Firma"
                      className="max-h-24 mx-auto"
                    />
                    <div className="text-xs text-center mt-2" style={{ color: 'var(--color-text-tertiary)' }}>
                      {istanza.firma_semplice_nominativo}
                      {istanza.firma_semplice_data && (
                        <> — {new Date(istanza.firma_semplice_data).toLocaleString('it-IT')}</>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {istanza && (
          <div
            className="flex items-center justify-between px-5 py-3 border-t flex-shrink-0"
            style={{ borderColor: 'var(--color-border)' }}
          >
            <div className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
              {istanza.compilato_da_nome && `Compilata da ${istanza.compilato_da_nome}`}
              {isDirty && (
                <span className="ml-2" style={{ color: 'var(--color-warning, #f59e0b)' }}>
                  (modifiche non salvate)
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* Condividi link esterno */}
              {!shareLink ? (
                <button
                  onClick={handleShareLink}
                  disabled={shareLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition-colors disabled:opacity-40"
                  style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' }}
                >
                  {shareLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                  Condividi
                </button>
              ) : (
                <button
                  onClick={handleCopyLink}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition-colors"
                  style={{
                    borderColor: copied ? 'var(--color-success, #22c55e)' : 'var(--color-primary)',
                    color: copied ? 'var(--color-success, #22c55e)' : 'var(--color-primary)',
                  }}
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'Copiato!' : 'Copia link'}
                </button>
              )}
              {!isReadOnly && (
                <button
                  onClick={handleSave}
                  disabled={saving || !isDirty}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition-colors disabled:opacity-40"
                  style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' }}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Salva
                </button>
              )}
              {canComplete && (
                <button
                  onClick={handleComplete}
                  disabled={completing}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg text-white transition-colors disabled:opacity-40"
                  style={{ backgroundColor: 'var(--color-primary)' }}
                >
                  {completing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                  Completa
                </button>
              )}
              {canSign && (
                <button
                  onClick={() => setShowFirma(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg text-white transition-colors"
                  style={{ backgroundColor: 'var(--color-success, #22c55e)' }}
                >
                  <PenTool className="w-4 h-4" />
                  Firma
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Firma Modal */}
      {showFirma && (
        <FirmaSempliceModal
          onClose={() => setShowFirma(false)}
          onConfirm={handleFirma}
          loading={firmaLoading}
        />
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  )
}
